import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Typography,
  IconButton,
  Drawer,
  Chip,
  Button,
} from '@mui/material';
import {
  green,
  orange,
} from '@mui/material/colors';

import {RequestStatusId} from '../../../ApplicationConfig/constants'

import {useTranslation} from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from "react-router-dom"
import {Language} from '../../../ApplicationConfig/constants'
import {FormatPhoneNumber} from '../../../Utilities/functions'

import {formatDate} from '../../../Utilities/functions';

const PREFIX = 'SummaryDrawer';

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerHeader: `${PREFIX}-drawerHeader`,
  drawerLabel: `${PREFIX}-drawerLabel`,
  drawerContent: `${PREFIX}-drawerContent`,
  drawerSection: `${PREFIX}-drawerSection`,
  listIcon: `${PREFIX}-listIcon`,
  footer: `${PREFIX}-footer`,
  chip: `${PREFIX}-chip`,
  chipGreen: `${PREFIX}-chipGreen`,
  chipRed: `${PREFIX}-chipRed`,
  chipBlue: `${PREFIX}-chipBlue`,
  chipGrey: `${PREFIX}-chipGrey`,
  chipOrange: `${PREFIX}-chipOrange`
};

const StyledDrawer = styled(Drawer)((
  {
    theme
  }
) => ({
  [`&.${classes.drawer}`]: {
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      paddingLeft: theme.spacing(3),
    },
  },

  [`& .${classes.drawerHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.common.white,
    margin: theme.spacing(0, -3, 3, -3),
    boxShadow: theme.shadows[0],
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.divider,
    position: 'fixed',
    top: 0,
    zIndex: 500,
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 3),
  },

  [`& .${classes.drawerLabel}`]: {
    textTransform: 'uppercase',
  },

  [`& .${classes.drawerContent}`]: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(8),
    padding: theme.spacing(3, 3, 3, 0),
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
  },

  [`& .${classes.drawerSection}`]: {
    paddingTop: theme.spacing(2),
    flexShrink: 0,
  },

  [`& .${classes.listIcon}`]: {
    color: theme.palette.common.black,
  },

  [`& .${classes.footer}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: theme.spacing(-3),
    marginRight: theme.spacing(-3),
    padding: theme.spacing(1.75, 3),
    borderTop: '1px solid',
    borderTopColor: theme.palette.divider,
    position: 'fixed',
    bottom: 0,
    width: '400px',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.common.white,
    zIndex: 500,
  },

  [`& .${classes.chip}`]: {
    '& .MuiChip-label:first-letter': {
      textTransform: 'uppercase',
    },
  },

  [`& .${classes.chipGreen}`]: {
    backgroundColor: green[500],
    color: 'white',
  },

  [`& .${classes.chipRed}`]: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
  },

  [`& .${classes.chipBlue}`]: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },

  [`& .${classes.chipGrey}`]: {
    backgroundColor: theme.palette.grey[300],
    color: 'black',
  },

  [`& .${classes.chipOrange}`]: {
    backgroundColor: orange[500],
    color: 'white',
  }
}));

const DRAWER_WIDTH = 400;


export default function SummaryDrawer(props) {

  const {t,i18n} = useTranslation();
  let { lang } = useParams();
  var latestStatusLabel=''

  if (props.data==undefined){return <></>}

  switch (props.data.status.id){

    case RequestStatusId.Approved:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.approved');
      break;

    case RequestStatusId.Denied:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.denied');
      break;

    case RequestStatusId.DisclosureAnalysis:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.disclosureAnalysis');
      break;

    case RequestStatusId.Draft:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.draft');
      break;

    case RequestStatusId.Submitted:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.submitted');
      break;

    case RequestStatusId.UpdateRequest:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.updateRequest');
      break;

    case RequestStatusId.Withdrawn:
      latestStatusLabel=t('summaryDrawer.latestStatusLabel.withdraw');
      break;
  }

  const content = () => (
    <>
      <div className={classes.drawerHeader}>
        <Typography component="h2" variant="h6">
          {t('summaryDrawer.summary')}
          </Typography>
       
          <IconButton
            onClick={props.clickHandler}
            edge="end"
            id="details-close"
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
       
      </div>

        <div className={classes.drawerContent}>
          <Chip label={lang==Language.French?props.data.status.nameFr:props.data.status.nameEn}
            className={clsx({
              [classes.chipGreen]: props.data.status.id ===RequestStatusId.Approved,
              [classes.chipRed]: props.data.status.id === RequestStatusId.Denied,
              [classes.chipBlue]: props.data.status.id === RequestStatusId.Submitted,
              [classes.chipGrey]: props.data.status.id === RequestStatusId.Draft || props.data.status.id === RequestStatusId.Withdrawn,
              [classes.chipOrange]: props.data.status.id === RequestStatusId.DisclosureAnalysis || props.data.status.id === RequestStatusId.UpdateRequest,
            })} />

       
        <div className={classes.drawerSection}>
          <Typography 
          component="h3" 
          variant="subtitle2"
          className={classes.drawerLabel}
          
          >{t('summaryDrawer.projectName')}</Typography>
          <Typography variant="body2">{props.data.contractNumber}</Typography>
          <Typography variant="body2">{props.data.identifier}</Typography>
        </div>
        <div className={classes.drawerSection}>
          <Typography 
          component="h3" 
          variant="subtitle2"
          className={classes.drawerLabel}
          >{t('summaryDrawer.filesRequested')}</Typography>
          {props.data.fileSummary.map((file,index)=>{return <Typography key={index} variant="body2">{file}</Typography>})}
        </div>
        <div className={classes.drawerSection}>
          <Typography component="h3" variant="subtitle2" className={classes.drawerLabel}>{latestStatusLabel}</Typography>
          <Typography variant="body2">{formatDate(props.data.latestStatusDateFormat,i18n.language,true)}</Typography>
        </div>

        {props.data.primaryAnalyst.account.length>0 && <div className={classes.drawerSection}>
          <Typography component="h3" variant="subtitle2" className={classes.drawerLabel}>{t('summaryDrawer.principalAnalyst')}</Typography>
          <Typography variant="body2">{props.data.primaryAnalyst.fullName}</Typography>
          <Typography variant="body2">{props.data.primaryAnalyst.email}</Typography>
          <Typography variant="body2">+{FormatPhoneNumber(props.data.primaryAnalyst.phoneNumber)}</Typography>
        </div>}


        {props.data.billableHours.length >0 && <div className={classes.drawerSection}>
          <Typography component="h3" variant="subtitle2" className={classes.drawerLabel}>{t('summaryDrawer.billableHours')}</Typography>
          <Typography variant="body2">{(props.data.billableMinutes>0 || props.data.billableHours>0) && <>{props.data.billableHours}&nbsp;{t('summaryDrawer.labelBillableHours',{count:parseInt(props.data.billableHours)})}&nbsp;{props.data.billableMinutes}&nbsp;{t('summaryDrawer.labelBillableMinutes',{count:parseInt(props.data.billableMinutes)})}</>}</Typography>
        </div>}
      </div>
      {props.viewRequestClickHandler !=undefined &&   <div className={classes.footer}><Button variant="contained" color="primary" className={classes.drawerButton} onClick={()=>{props.viewRequestClickHandler(props.data.id)}}>
      {t('summaryDrawer.viewRequest')}
      </Button></div>}
     
    </>
  );

  return (
    <div>
      <StyledDrawer
        anchor='right'
        open={props.open}
        onKeyUp={(e) => {
          if (e.key === 'Escape') {
            props.clickHandler();
          }
        }}
        onBackdropClick={(e)=> {e.preventDefault();}}
        variant='temporary'
        className={classes.drawer}
      >
        {content()}
      </StyledDrawer>
    </div>
  );
}
SummaryDrawer.propTypes={
  data: PropTypes.object,
  clickHandler: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  viewRequestClickHandler: PropTypes.func,
}